import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { SavedList } from '@/lib/indexedDBService';
import { MaterialItem, StockPiece } from '@/types';
import { decimalToFraction } from '@/utils/measurementUtils';

/**
 * Generates a PDF for a cut list
 * @param savedList The saved list to generate a PDF for
 * @returns The generated PDF document
 */
export const generateCutListPDF = (savedList: SavedList): jsPDF => {
  try {
    // Create a new PDF document
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    // Add title
    doc.setFontSize(20);
    doc.text(savedList.name, 14, 22);

    // Add date
    doc.setFontSize(10);
    doc.text(`Date: ${new Date(savedList.date).toLocaleDateString()}`, 14, 30);

    // Add summary section
    doc.setFontSize(14);
    doc.text('Summary', 14, 40);

    // Add summary table
    const summaryData = [
      ['Total Materials', savedList.materials.length.toString()],
      ['Total Stock Pieces', savedList.result.totalStock.toString()],
      ['Material Utilization', `${savedList.result.overallUtilization.toFixed(1)}%`],
    ];

    autoTable(doc, {
      startY: 45,
      head: [['Metric', 'Value']],
      body: summaryData,
      theme: 'grid',
      headStyles: { fillColor: [66, 135, 245], textColor: 255 },
      margin: { left: 14, right: 14 },
    });

    // Get the Y position after the summary table
    let currentY = (doc as any).lastAutoTable.finalY + 15;

    // Add material requirements section
    doc.setFontSize(14);
    doc.text('Material Requirements', 14, currentY);
    currentY += 5;

    // Create material requirements table
    const materialReqsData = Object.values(savedList.result.categoryStats).map(stat => [
      stat.categoryName,
      stat.stockCount.toString(),
      `${stat.utilization.toFixed(1)}%`,
    ]);

    autoTable(doc, {
      startY: currentY,
      head: [['Material Type', 'Stock Lengths Required', 'Utilization']],
      body: materialReqsData,
      theme: 'grid',
      headStyles: { fillColor: [66, 135, 245], textColor: 255 },
      margin: { left: 14, right: 14 },
    });

    // Get the Y position after the material requirements table
    currentY = (doc as any).lastAutoTable.finalY + 15;

    // Add cut list section
    doc.setFontSize(14);
    doc.text('Cut List', 14, currentY);
    currentY += 5;

    // Process each category
    for (const stat of Object.values(savedList.result.categoryStats)) {
      // Add category header
      doc.setFontSize(12);
      doc.text(stat.categoryName, 14, currentY);
      currentY += 5;

      // Get stock pieces for this category
      const stockPieces = savedList.result.stockPieces.filter(
        stock => stock.categoryName === stat.categoryName
      );

      // Create cut list table for this category
      const cutListData: any[][] = [];

      stockPieces.forEach((stock, stockIndex) => {
        // Add a row for the stock piece header
        cutListData.push([
          `Stock Piece #${stockIndex + 1}`,
          `Utilization: ${stock.utilization.toFixed(1)}%`,
          '',
          '',
        ]);

        // Add rows for each cut
        stock.cuts.forEach(cut => {
          cutListData.push(['', cut.name, `${decimalToFraction(cut.length)}"`, `Position: ${decimalToFraction(cut.position)}"`]);
        });

        // Add row for drop if applicable
        if (stock.remainingLength > 0) {
          cutListData.push(['', 'Drop', `${decimalToFraction(stock.remainingLength)}"`, '']);
        }

        // Add an empty row for spacing between stock pieces
        cutListData.push(['', '', '', '']);
      });

      // Add the table to the PDF
      autoTable(doc, {
        startY: currentY,
        head: [['Stock Piece', 'Item', 'Length', 'Position']],
        body: cutListData,
        theme: 'grid',
        headStyles: { fillColor: [66, 135, 245], textColor: 255 },
        margin: { left: 14, right: 14 },
        didParseCell: function(data) {
          // Style the stock piece header rows
          if (data.row.index % (4 + 1) === 0 && data.column.index === 0 && data.cell.text[0]?.startsWith('Stock Piece')) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = [240, 240, 240];
          }
        }
      });

      // Update current Y position
      currentY = (doc as any).lastAutoTable.finalY + 10;

      // Check if we need to add a new page
      if (currentY > doc.internal.pageSize.getHeight() - 20) {
        doc.addPage();
        currentY = 20;
      }
    }

    // Add footer
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    const footerText = `Generated with ServicePane on ${new Date().toLocaleDateString()}`;
    doc.text(footerText, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });

    return doc;
  } catch (error) {
    console.error('Error generating cut list PDF:', error);
    throw new Error(`Failed to generate PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};
